/**
 * Block lettering for the numbers the plates never needed.
 *
 * Every word on the Bingkai Kenangan plates is stitched from square blocks one
 * block wide — the swans' cursive, the ribbons' "how to pay", the "RM" on the
 * cash note. None of them carries a digit, and a price in a system font would
 * be the one thing on screen that is not stitched. So the digits and the "RM"
 * are drawn here on the same kind of grid, seven blocks tall, one block stroke,
 * and rendered by `BkPixel` as flat squares in the plates' own ink.
 */
export type PixelRows = readonly string[]

const G: Record<string, PixelRows> = {
  '0': ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '#.#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '..#..', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],
  '.': ['.', '.', '.', '.', '.', '.', '#'],
  x: ['...', '...', '#.#', '.#.', '#.#', '...', '...'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  M: ['#...#', '##.##', '#.#.#', '#.#.#', '#...#', '#...#', '#...#'],
  ' ': ['..', '..', '..', '..', '..', '..', '..'],
  ':': ['.', '#', '.', '.', '.', '#', '.'],
  '-': ['...', '...', '...', '###', '...', '...', '...'],
  '!': ['#', '#', '#', '#', '#', '.', '#'],
  '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
  "'": ['#', '#', '.', '.', '.', '.', '.'],
  ',': ['.', '.', '.', '.', '.', '#', '#'],
  '/': ['....#', '...#.', '...#.', '..#..', '.#...', '.#...', '#....'],
  // A zero with a stroke, for codes only (see stitchCode): on the keypad a
  // plain 0 and O are the same shape, and a code is read character by character.
  Ø: ['.###.', '#..##', '#.#.#', '#.#.#', '#.#.#', '##..#', '.###.'],
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.####'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'],
  J: ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  N: ['#...#', '#...#', '##..#', '#.#.#', '#..##', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '#.#.#', '.#.#.'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####']
}

/**
 * One printed strip: a frame of four photo windows, as on the cut plate's own
 * strip illustrations. A tag shows one of these per print, so the choice reads
 * without a word of copy — the booth's rule is a glyph, not a sentence.
 */
export const BK_STRIP_ICON: PixelRows = [
  '#####',
  '#...#',
  '#...#',
  '#####',
  '#...#',
  '#...#',
  '#####',
  '#...#',
  '#...#',
  '#####',
  '#...#',
  '#...#',
  '#####'
]

/** True when every character has a glyph, so the text can be stitched. */
export const canStitch = (text: string): boolean => [...text].every((c) => c in G)

/** Set a line of text: glyphs side by side, one empty block between them. */
export function stitchText(text: string): PixelRows {
  const glyphs = [...text].map((c) => G[c] ?? G[' '])
  return Array.from({ length: 7 }, (_, row) => glyphs.map((g) => g[row]).join('.'))
}

/** Lay pieces side by side, bottom-aligned, `gap` blocks apart. */
export function besideEachOther(pieces: PixelRows[], gap = 1): PixelRows {
  const height = Math.max(...pieces.map((p) => p.length))
  return Array.from({ length: height }, (_, row) =>
    pieces
      .map((p) => {
        const r = row - (height - p.length)
        return r >= 0 ? p[r] : '.'.repeat(p[0].length)
      })
      .join('.'.repeat(gap))
  )
}

/** A code as typed: capitals, and the stroked zero so 0 never reads as O. */
export const stitchCode = (code: string): PixelRows => stitchText(code.replace(/0/g, 'Ø'))

/**
 * Free text — a gateway message, say — in capitals, wrapped at spaces to lines
 * of at most `maxBlocks` blocks. Characters with no glyph become spaces rather
 * than holes that shift the rest of the line.
 */
export function stitchLines(text: string, maxBlocks: number): PixelRows[] {
  const clean = [...text.toUpperCase().replace(/[—–]/g, '-').replace(/[’‘]/g, "'")]
    .map((c) => (c in G ? c : ' '))
    .join('')
  const width = (t: string): number =>
    [...t].reduce((n, c) => n + G[c][0].length, 0) + Math.max(0, t.length - 1)
  const lines: string[] = []
  let line = ''
  for (const word of clean.split(/ +/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word
    if (line && width(next) > maxBlocks) {
      lines.push(line)
      line = word
    } else line = next
  }
  if (line) lines.push(line)
  return lines.map(stitchText)
}

/** "15" for whole ringgit, "12.50" when there are sen. */
export const priceDigits = (price: number): string =>
  Number.isInteger(price) ? String(price) : price.toFixed(2)
